import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BIZ = {
  name: "Min3rva.org",
  email: "support@loompdf.com",
  address: "Bengaluru, Karnataka, India",
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Your message is on its way — we'll reply soon. ✨");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please email support@loompdf.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative min-h-screen grain" data-testid="contact-page">
      <Nav />
      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-24">
        <span className="tag-pill">contact &amp; support</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-4">We're here to help</h1>
        <p className="text-stone-600 mt-3 max-w-2xl text-lg">
          Question about your account, a payment, or a study deck? Send us a message and we'll get back to you.
        </p>

        <div className="mt-10 grid md:grid-cols-5 gap-8">
          {/* FORM */}
          <form onSubmit={submit} className="md:col-span-3 sticky-card p-8 space-y-5" data-testid="contact-form">
            <div>
              <Label htmlFor="c-name">Your name</Label>
              <Input id="c-name" value={form.name} onChange={set("name")} placeholder="Jane Student"
                     className="mt-1.5" data-testid="contact-name-input" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com"
                     className="mt-1.5" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="c-message">Message</Label>
              <Textarea id="c-message" value={form.message} onChange={set("message")} rows={6}
                        placeholder="How can we help?" className="mt-1.5" data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={sending}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="contact-submit-button">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>

          {/* DETAILS */}
          <div className="md:col-span-2 space-y-5">
            <div className="sticky-card p-7 space-y-4" data-testid="contact-details">
              <div className="font-display text-2xl">Other ways to reach us</div>
              <div className="flex items-center gap-3 text-stone-800">
                <Mail className="w-4 h-4 text-indigo-500 shrink-0" />
                <a href={`mailto:${BIZ.email}`} className="underline" data-testid="contact-email-link">{BIZ.email}</a>
              </div>
              <div className="flex items-center gap-3 text-stone-800">
                <MapPin className="w-4 h-4 text-indigo-500 shrink-0" /><span>{BIZ.address}</span>
              </div>
              <p className="text-stone-500 text-sm">We typically respond within 1–2 business days.</p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 text-sm text-stone-500">
          <Link to="/terms" className="hover:text-stone-900 underline">Terms</Link>
          <Link to="/privacy" className="hover:text-stone-900 underline">Privacy</Link>
          <Link to="/refund" className="hover:text-stone-900 underline">Refund &amp; Cancellation</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
